import { useDismiss } from "@/common/hooks/useDismiss";
import type { AccountRole } from "@/features/accounts/api/accountsApi";
import { useChangeRole } from "@/features/accounts/api/membersApi";
import { ChevronDown, X } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

type RoleOption = { value: AccountRole; label: string };

function getRoleOptions(currentUserRole: AccountRole): RoleOption[] {
  const options: RoleOption[] = [
    { value: "admin", label: "Admin" },
    { value: "operator", label: "Operador" },
    { value: "viewer", label: "Visor" },
  ];
  // owner can assign any role except owner; admin cannot assign owner either
  if (currentUserRole === "owner") {
    return options; // owner sees admin/operator/viewer (not owner—can't transfer ownership in v1)
  }
  return options.filter((o) => o.value !== "owner");
}

type Props = {
  accountId: string;
  membershipId: string;
  memberEmail: string;
  currentRole: AccountRole;
  currentUserRole: AccountRole;
  open: boolean;
  onClose: () => void;
};

export function ChangeRoleDialog({
  accountId,
  membershipId,
  memberEmail,
  currentRole,
  currentUserRole,
  open,
  onClose,
}: Props) {
  const [selectedRole, setSelectedRole] = useState<AccountRole>(currentRole);
  const [roleOpen, setRoleOpen] = useState(false);
  const roleSelectorRef = useRef<HTMLDivElement>(null);
  const mutation = useChangeRole(accountId);

  useDismiss({
    enabled: roleOpen,
    containerRef: roleSelectorRef,
    onDismiss: () => setRoleOpen(false),
  });

  if (!open) return null;

  const roleOptions = getRoleOptions(currentUserRole);
  const selectedLabel = roleOptions.find((o) => o.value === selectedRole)?.label ?? "";

  async function handleConfirm() {
    if (selectedRole === currentRole) {
      onClose();
      return;
    }
    try {
      await mutation.mutateAsync({ membershipId, role: selectedRole });
      toast.success(`Rol de ${memberEmail} cambiado a ${selectedLabel}.`);
      onClose();
    } catch {
      toast.error("No se pudo cambiar el rol. Intentá de nuevo.");
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
        }}
      />
      <div className="relative z-10 w-full max-w-md rounded-2xl border border-app-border bg-app-surface p-6 shadow-xl">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-semibold text-app-ink-1 text-lg tracking-tight">Cambiar rol</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-app-ink-3 hover:bg-app-surface-2"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="mb-4 text-app-ink-2 text-sm">
          Cambiar el rol de <strong>{memberEmail}</strong>:
        </p>

        <div ref={roleSelectorRef} className="relative mb-6">
          <button
            type="button"
            onClick={() => setRoleOpen((o) => !o)}
            className="flex h-11 w-full items-center justify-between rounded-xl border border-app-border bg-app-bg px-4 text-app-ink-1 text-base focus:outline-none"
            aria-haspopup="listbox"
            aria-expanded={roleOpen}
          >
            <span>{selectedLabel}</span>
            <ChevronDown className="h-4 w-4 text-app-ink-3" />
          </button>
          {roleOpen && (
            <ul className="absolute top-full right-0 left-0 z-20 mt-1 overflow-hidden rounded-xl border border-app-border bg-app-surface shadow">
              {roleOptions.map((opt) => (
                <li
                  key={opt.value}
                  aria-selected={opt.value === selectedRole}
                  onClick={() => {
                    setSelectedRole(opt.value);
                    setRoleOpen(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setSelectedRole(opt.value);
                      setRoleOpen(false);
                    }
                  }}
                  className={`cursor-pointer px-4 py-2.5 text-sm transition-colors hover:bg-app-surface-2 ${
                    opt.value === selectedRole ? "font-medium text-primary-600" : "text-app-ink-1"
                  }`}
                >
                  {opt.label}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex h-11 flex-1 items-center justify-center rounded-xl border border-app-border font-medium text-app-ink-2 text-sm transition-colors hover:bg-app-surface-2"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={mutation.isPending}
            className="flex h-11 flex-1 items-center justify-center rounded-xl bg-app-ink-1 font-medium text-sm text-white transition-colors hover:bg-[#1e211d] disabled:opacity-60"
          >
            {mutation.isPending ? "Guardando…" : "Confirmar"}
          </button>
        </div>
      </div>
    </div>
  );
}
